import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BoardView, TotalDevsPill } from '../components/work/BoardView';
import { CardDialog } from '../components/work/CardDialog';
import { useToast } from '../components/Toast';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { formatDateTime } from '../lib/format';
import { boardQueryKey, commitQueryKey, commitsQueryKey, getCommit, isApiStatus, problemDetail, restoreCommit } from '../lib/workApi';

const linkClass = 'text-atg-blue font-medium hover:underline whitespace-nowrap';
const noop = () => {};
const btnRestore =
  'px-3 py-1.5 rounded text-sm text-white bg-atg-blue hover:bg-atg-navy disabled:opacity-50 focus:outline-none focus-visible:ring-2 ring-atg-blue';
const btnConfirm =
  'px-3 py-1.5 rounded text-sm text-white bg-red-700 hover:bg-red-800 disabled:opacity-50 focus:outline-none focus-visible:ring-2 ring-red-700';

function BackLink() {
  return <Link to="/commits" className={linkClass}>← Back to commits</Link>;
}

/**
 * One board snapshot, read-only: the same board components with canWrite off, no drag sensors
 * and no mutations. Cards open in view mode against the snapshot's board.
 */
export function CommitView() {
  const { id } = useParams();
  const commitId = Number(id);
  const validId = Number.isInteger(commitId) && commitId > 0;
  const commitQ = useQuery({
    queryKey: commitQueryKey(commitId),
    queryFn: () => getCommit(commitId),
    enabled: validId,
    retry: (count, err) => !isApiStatus(err, 404) && count < 3,
  });
  const [openCardId, setOpenCardId] = useState<number | null>(null);

  // Restore (Write users): two-step confirm, then the server takes a safety commit and rewrites the board.
  const canWrite = useIsAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [confirmRestore, setConfirmRestore] = useState(false);
  const restore = useMutation({
    mutationFn: () => restoreCommit(commitId),
    onSuccess: (safety) => {
      void qc.invalidateQueries({ queryKey: boardQueryKey });
      void qc.invalidateQueries({ queryKey: commitsQueryKey });
      toast.success(`Board restored from this snapshot. A safety snapshot (#${safety.id}) was taken first.`, 8000);
      navigate('/');
    },
    onError: (err) => {
      setConfirmRestore(false);
      toast.error(problemDetail(err, "Couldn't restore the board. Nothing was changed."));
    },
  });

  if (!validId || isApiStatus(commitQ.error, 404)) {
    return (
      <div className="w-[95%] mx-auto text-sm">
        <p className="text-slate-700 mb-2">This commit no longer exists.</p>
        <BackLink />
      </div>
    );
  }
  if (commitQ.isLoading) {
    return <div className="w-[95%] mx-auto text-slate-500 text-sm">Loading…</div>;
  }
  if (commitQ.isError || !commitQ.data) {
    return (
      <div className="w-[95%] mx-auto text-sm">
        <p className="text-red-700 mb-2" role="alert">Couldn't load this commit. Refresh the page to try again.</p>
        <BackLink />
      </div>
    );
  }

  const commit = commitQ.data;
  const board = commit.board;
  const hasStreams = board.streams.length > 0;

  return (
    <div className="w-[95%] mx-auto">
      <div className="bg-slate-100 border border-slate-200 rounded px-4 py-2 flex items-center gap-3 mb-4 text-sm" id="commit-banner">
        <span className="text-slate-800">
          Snapshot from <strong>{formatDateTime(commit.createdAt)}</strong>
          {commit.createdBy ? <> by <strong>{commit.createdBy}</strong></> : null}
        </span>
        {commit.note && <span className="italic text-slate-600 truncate min-w-0">{commit.note}</span>}
        <span className="ml-auto flex items-center gap-4">
          <BackLink />
          <Link to="/" className={linkClass}>Current board</Link>
        </span>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold text-atg-navy">Board snapshot</h1>
        {hasStreams && <TotalDevsPill board={board} />}
        <span className="text-xs text-slate-500 border border-slate-300 rounded-full px-2">View only</span>
        {canWrite && (
          <span className="ml-auto flex items-center gap-2">
            {confirmRestore && !restore.isPending && (
              <>
                <span className="text-sm text-slate-700">Replace the current board with this snapshot?</span>
                <button type="button" onClick={() => setConfirmRestore(false)}
                  className="px-3 py-1.5 rounded border border-slate-300 text-sm text-slate-700 bg-white hover:bg-slate-100">
                  Cancel
                </button>
              </>
            )}
            <button
              type="button"
              id="restore-board"
              disabled={restore.isPending}
              onClick={() => (confirmRestore ? restore.mutate() : setConfirmRestore(true))}
              className={confirmRestore ? btnConfirm : btnRestore}
            >
              {restore.isPending ? 'Restoring…' : confirmRestore ? 'Confirm restore' : 'Restore this snapshot'}
            </button>
          </span>
        )}
      </div>

      {hasStreams ? (
        <BoardView
          board={board}
          canWrite={false}
          onOpenCard={setOpenCardId}
          onEditStream={noop}
          onAdjustWorkers={noop}
        />
      ) : (
        <div className="border border-dashed border-slate-300 rounded-lg py-16 text-center text-slate-600">
          <p className="font-medium text-slate-800">This snapshot has no work streams.</p>
        </div>
      )}

      {openCardId !== null && (
        <CardDialog
          key={openCardId}
          board={board}
          cardId={openCardId}
          canWrite={false}
          onClose={() => setOpenCardId(null)}
          onShowCard={setOpenCardId}
        />
      )}
    </div>
  );
}
